import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import { moduleConfig } from './requisitionsModuleConfig';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../../services/paginatedList';

export interface SupplyRequisitionLine {
  id?: number;
  itemId?: number | null;
  itemVcode?: string | null;
  itemDescr?: string | null;
  unitId?: number | null;
  qty?: number | null;
  rob?: number | null;
  itemNotes?: string | null;
  item?: {
    level1?: string | null;
    level2?: string | null;
    level3?: string | null;
  } | null;
  lastOrdered?: {
    purchaseOrderId?: number | null;
    purchaseOrderRef?: string | null;
    orderDate?: string | null;
    qty?: number | null;
  } | null;
  lastRequested?: {
    requisitionId?: number | null;
    requisitionRef?: string | null;
    reqDate?: string | null;
    qty?: number | null;
  } | null;
  unit?: { id?: number; ucode?: string | null; descr?: string | null } | null;
}

export type RequisitionSaveStatus = 'DRAFT' | 'SAVED';

export interface SupplyRequisitionTemplateLine {
  id?: number;
  itemId?: number | null;
  itemVcode?: string | null;
  itemDescr?: string | null;
  unitId?: number | null;
  qty?: number | null;
  rob?: number | null;
  itemNotes?: string | null;
  item?: { level1?: string | null; level2?: string | null; level3?: string | null } | null;
}

export interface SupplyRequisitionTemplate {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  lines?: SupplyRequisitionTemplateLine[];
}

export interface SupplyRequisition {
  id: number;
  reqDate?: string | null;
  caseId?: number | null;
  vesselId?: number | null;
  categoryIds?: number[];
  ref?: string | null;
  refN?: number | null;
  notes?: string | null;
  saveStatus?: RequisitionSaveStatus | null;
  createdAt: string;
  updatedAt: string;
  lines?: SupplyRequisitionLine[];
  _count?: { lines: number };
  supplyCase?: {
    id: number;
    ref?: string | null;
    caseDate?: string | null;
    eta?: string | null;
    port?: { name?: string | null; code?: string | null } | null;
  } | null;
  vessel?: { id: number; vesselName: string } | null;
  categories?: { id: number; name: string; abrv?: string | null }[];
  pipelineCounts?: {
    enquiryCount?: number;
    quotationCount?: number;
    orderCount?: number;
    deliveryCount?: number;
  };
}

export const useSupplyRequisitions = (
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) => {
  const q = buildListQuery(searchParams, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<
    PaginatedApiResponse<SupplyRequisition> | SupplyRequisition[]
  >(
    `${moduleConfig.module.api.list}${q}`,
    fetcher,
    { revalidateOnFocus: true },
  );
  const normalized = normalizePaginatedListResponse<SupplyRequisition>(data, page, limit);
  return {
    data: normalized.data,
    clients: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useSupplyRequisition = (id?: number) => {
  const { data, error, isLoading, mutate } = useSWR<SupplyRequisition>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, item: data, isLoading, isError: error, mutate };
};

